import React, { useState, useEffect } from 'react';
import { Trophy, Crown, Star } from 'lucide-react';
import { api } from '../utils/api';

interface HonorUser {
  userId: number;
  username: string;
  isAnonymous: boolean;
  totalAmount: number;
  donationCount: number;
  lastDonation: string;
  level?: {
    name: string;
    color: string;
  };
}

interface HonorListProps {
  className?: string;
}

const HonorList: React.FC<HonorListProps> = ({ className = '' }) => {
  const [type, setType] = useState<'total' | 'monthly'>('total');
  const [honorList, setHonorList] = useState<HonorUser[]>([]);
  const [loading, setLoading] = useState(true);

  const fetchHonorList = async () => {
    try {
      setLoading(true);
      const response = await api.get(`/reconciliation/honor-list?type=${type}`);
      if (response?.success && response?.data) {
        setHonorList(response.data);
      }
    } catch (error) {
      console.error('获取荣誉榜失败:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchHonorList();
  }, [type]);

  // 获取荣誉等级
  const getHonorLevel = (amount: number) => {
    if (amount >= 1000) {
      return { name: '至尊', color: '#E6E8FA' };
    }
    if (amount >= 200) {
      return { name: '铂金', color: '#E5E4E2' };
    }
    if (amount >= 50) {
      return { name: '黄金', color: '#FFD700' };
    }
    if (amount >= 10) {
      return { name: '白银', color: '#C0C0C0' };
    }
    if (amount >= 1) {
      return { name: '青铜', color: '#CD7F32' };
    }
    return { name: '新人', color: '#9E9E9E' };
  };

  // 获取排名图标
  const getRankIcon = (index: number) => {
    if (index === 0) {
      return <Crown className="w-6 h-6 text-yellow-500" />;
    }
    if (index === 1) {
      return <Trophy className="w-6 h-6 text-gray-400" />;
    }
    if (index === 2) {
      return <Star className="w-6 h-6 text-amber-700" />;
    }
    return <span className="text-lg font-bold text-gray-500 w-8 text-center">{index + 1}</span>;
  };

  return (
    <div className={`bg-white rounded-lg shadow ${className}`}>
      {/* 头部 */}
      <div className="p-4 border-b border-gray-200">
        <h2 className="text-xl font-bold text-gray-800 mb-3">🏆 荣誉榜</h2>
        
        {/* 类型切换 */}
        <div className="flex bg-gray-100 rounded-lg p-1">
          <button
            onClick={() => setType('total')}
            className={`flex-1 py-2 px-4 rounded-md text-sm font-medium transition-colors ${
              type === 'total'
                ? 'bg-white text-pink-600 shadow'
                : 'text-gray-600 hover:text-gray-800'
            }`}
          >
            总榜
          </button>
          <button
            onClick={() => setType('monthly')}
            className={`flex-1 py-2 px-4 rounded-md text-sm font-medium transition-colors ${
              type === 'monthly'
                ? 'bg-white text-pink-600 shadow'
                : 'text-gray-600 hover:text-gray-800'
            }`}
          >
            月榜
          </button>
        </div>
      </div>

      {/* 内容 */}
      <div className="p-4">
        {loading ? (
          <div className="flex justify-center py-8">
            <div className="w-8 h-8 border-4 border-pink-500 border-t-transparent rounded-full animate-spin"></div>
          </div>
        ) : honorList.length === 0 ? (
          <div className="text-center py-8 text-gray-500">
            暂无捐助记录
          </div>
        ) : (
          <div className="space-y-3">
            {/* 前三名特别样式 */}
            {honorList.slice(0, 3).map((user, index) => (
              <div
                key={user.userId}
                className={`flex items-center gap-3 p-3 rounded-lg border-2 ${
                  index === 0
                    ? 'border-yellow-400 bg-yellow-50'
                    : index === 1
                    ? 'border-gray-300 bg-gray-50'
                    : 'border-amber-600 bg-amber-50'
                }`}
              >
                <div className="flex-shrink-0">
                  {getRankIcon(index)}
                </div>
                
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <span className="font-semibold text-gray-800 truncate">
                      {user.isAnonymous ? '匿名用户' : user.username}
                    </span>
                    <span
                      className="text-xs px-2 py-0.5 rounded-full"
                      style={{ backgroundColor: getHonorLevel(user.totalAmount).color }}
                    >
                      {getHonorLevel(user.totalAmount).name}
                    </span>
                  </div>
                  <div className="text-sm text-gray-500">
                    捐助 {user.donationCount} 次
                  </div>
                </div>
                
                <div className="text-right flex-shrink-0">
                  <div className="font-bold text-pink-600">
                    {Number(user.totalAmount).toFixed(2)} USDT
                  </div>
                </div>
              </div>
            ))}

            {/* 4-10名 */}
            {honorList.slice(3, 10).map((user, index) => (
              <div
                key={user.userId}
                className="flex items-center gap-3 p-3 rounded-lg border border-gray-200"
              >
                <div className="flex-shrink-0">
                  {getRankIcon(index + 3)}
                </div>
                
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <span className="font-medium text-gray-800 truncate">
                      {user.isAnonymous ? '匿名用户' : user.username}
                    </span>
                    <span
                      className="text-xs px-2 py-0.5 rounded-full"
                      style={{ backgroundColor: getHonorLevel(user.totalAmount).color }}
                    >
                      {getHonorLevel(user.totalAmount).name}
                    </span>
                  </div>
                  <div className="text-sm text-gray-500">
                    捐助 {user.donationCount} 次
                  </div>
                </div>
                
                <div className="text-right flex-shrink-0">
                  <div className="font-medium text-gray-700">
                    {Number(user.totalAmount).toFixed(2)} USDT
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default HonorList;
